import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
// import { Proposals } from '../types.ts';
import { FileText, Calendar, Clock } from 'lucide-react';

interface ProposalCardProps {
  proposal: any; // Changed from Proposals to any
  onClick?: (id: string) => void;
}

const CardContainer = styled(motion(Box))(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(5px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    transform: 'scale(1.02)',
    boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
    border: '1px solid rgba(0, 255, 255, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  }
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  border: '2px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: '2px solid rgba(0, 255, 255, 0.3)',
    boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
    background: 'rgba(26, 26, 46, 0.8)',
  }
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  color: '#8a2be2',
  border: '1px solid rgba(138, 43, 226, 0.2)',
  margin: theme.spacing(0.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(26, 26, 46, 0.8)',
    borderColor: 'rgba(0, 255, 255, 0.3)',
    color: '#00ffff',
    boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
  }
}));

const ProposalCard: React.FC<ProposalCardProps> = ({ proposal, onClick }) => {
  // Ensure amount is a number to prevent toLocaleString errors
  const safeAmount = typeof proposal.amount === 'number' ? proposal.amount : 0;
  
  return (
    <CardContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onClick={() => onClick && onClick(proposal.id)}
    >
      <IconContainer>
        <FileText 
          size={24} 
          color="#00ffff"
          style={{ transition: 'all 0.3s ease' }}
        />
      </IconContainer>
      
      <Typography 
        variant="h6" 
        sx={{ 
          color: '#00ffff',
          textShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#00ffff',
            textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
          }
        }}
      >
        {proposal.title}
      </Typography>
      
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'rgba(255, 255, 255, 0.6)',
          mb: 2,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: 'rgba(255, 255, 255, 0.8)',
          }
        }}
      >
        {proposal.description}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            '& svg': {
              color: '#00ffff !important',
            },
            '& .MuiTypography-caption': {
              color: 'rgba(255, 255, 255, 0.8)',
            }
          }
        }}>
          <Calendar size={16} color="#8a2be2" />
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              transition: 'all 0.3s ease',
            }}
          >
            {new Date(proposal.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            '& svg': {
              color: '#00ffff !important',
            },
            '& .MuiTypography-caption': {
              color: 'rgba(255, 255, 255, 0.8)',
            }
          }
        }}>
          <Clock size={16} color="#8a2be2" />
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              transition: 'all 0.3s ease',
            }}
          >
            {proposal.category}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <StatusChip 
          label={proposal.status}
          size="small"
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#8a2be2',
            textShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
            transition: 'all 0.3s ease',
            '&:hover': {
              color: '#00ffff',
              textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
            }
          }}
        >
          ${safeAmount.toLocaleString()}
        </Typography>
      </Box>
    </CardContainer>
  );
};

export default ProposalCard; 